/**
 * Customer-scope routes — the pages that mount inside CustomerLayout for a
 * single customer: the plant/user/report directories, customer dashboards
 * (operations / management), the unified customer map, per-user settings, customer settings,
 * and the sales/returns contact-form pages. The contact-form routes are
 * gated on the combined sales+returns exclude flags so they drop only when
 * both areas are excluded from a tenant build.
 *
 * Contains:
 * - customerRoutes — a fragment of <Route> elements for the composer
 */
import React from 'react';
import { Route } from 'react-router-dom';

import CustomerLayout from '@sophocles/components/Layout/CustomerLayout';
import {
  determineCanViewUsers,
  determineCustomerAccess,
  determineIsODLEngineering,
  determineIsStaff,
} from '@sophocles/router/checkCredentials';
import CustomerHomeRedirect from '@sophocles/router/CustomerHomeRedirect';
import CustomerMapRedirect from '@sophocles/router/CustomerMapRedirect';
import {
  customerContactCreatePath,
  customerContactPath,
  customerHomeRedirectPath,
  customerPath,
  customerSettingsPath,
  customerSettingsSectionPath,
} from '@sophocles/router/paths';
import RequireAuth from '@sophocles/router/RequireAuth';
import ManagementDashboardView from '@sophocles/views/dashboards/ManagementDashboardView';
import OperationsDashboardView from '@sophocles/views/dashboards/OperationsDashboardView';
import CustomerMapView from '@sophocles/views/map/CustomerMapView';
import CustomerAlarmLiveReport from '@sophocles/views/reports/CustomerAlarmLiveReportView';
import CustomerCurfewReportView from '@sophocles/views/reports/CustomerCurfewReportView';
import CustomerReportsView from '@sophocles/views/reports/CustomerReportsView';
import ContactFormView from '@sophocles/views/sales/ContactFormView';
import CustomerSettingsPage from '@sophocles/views/settings/CustomerSettingsViewV2';
import UserSettingsPage from '@sophocles/views/settings/UserSettingsViewV2';
import SiteGroupGridView from '@sophocles/views/siteGroups/SiteGroupGridView';
import CreateUserView from '@sophocles/views/users/CreateUserView';
import CustomerUserGridView from '@sophocles/views/users/CustomerUserGridView';
import EditPasswordView from '@sophocles/views/users/EditPasswordView';

export const customerRoutes = (
  <>
    <Route path={customerHomeRedirectPath.pattern} element={<CustomerHomeRedirect />} />

    <Route
      path={customerPath.pattern}
      element={<RequireAuth component={CustomerLayout} onEnter={determineCustomerAccess} />}
    >
      <Route path="plants" element={<RequireAuth component={SiteGroupGridView} onEnter={determineCustomerAccess} />} />
      <Route path="users" element={<RequireAuth component={CustomerUserGridView} onEnter={determineCanViewUsers} />} />
      <Route
        path="reports"
        element={<RequireAuth component={CustomerReportsView} onEnter={determineCustomerAccess} />}
      />
      <Route
        path="reports/alarmlivesummary"
        element={<RequireAuth component={CustomerAlarmLiveReport} onEnter={determineCustomerAccess} />}
      />
      <Route
        path="reports/curfew"
        element={<RequireAuth component={CustomerCurfewReportView} onEnter={determineCustomerAccess} />}
      />
      {/* Customer Dashboards */}
      <Route
        path="dashboards/operations"
        element={<RequireAuth component={OperationsDashboardView} onEnter={determineCustomerAccess} />}
      />
      <Route
        path="dashboards/management"
        element={<RequireAuth component={ManagementDashboardView} onEnter={determineIsODLEngineering} />}
      />
      {/* The customer map replaces the old map dashboard; the old URL redirects. */}
      <Route path="dashboards/map" element={<CustomerMapRedirect />} />
      <Route path="map" element={<RequireAuth component={CustomerMapView} onEnter={determineCustomerAccess} />} />
      <Route
        path="map/site/:site_id"
        element={<RequireAuth component={CustomerMapView} onEnter={determineCustomerAccess} />}
      />
      <Route path="users/new" element={<RequireAuth component={CreateUserView} onEnter={determineCanViewUsers} />} />
      <Route
        path="users/:user_id"
        element={<RequireAuth component={UserSettingsPage} onEnter={determineCanViewUsers} />}
      />
      <Route
        path="users/:user_id/settings"
        element={<RequireAuth component={UserSettingsPage} onEnter={determineCanViewUsers} />}
      />
      <Route
        path="users/:user_id/settings/:section"
        element={<RequireAuth component={UserSettingsPage} onEnter={determineCanViewUsers} />}
      />
      <Route
        path="users/:user_id/manualpassword"
        element={<RequireAuth component={EditPasswordView} onEnter={determineIsStaff} />}
      />
    </Route>
    <Route
      path={customerSettingsPath.pattern}
      element={<RequireAuth component={CustomerSettingsPage} onEnter={determineCustomerAccess} />}
    />
    <Route
      path={customerSettingsSectionPath.pattern}
      element={<RequireAuth component={CustomerSettingsPage} onEnter={determineCustomerAccess} />}
    />
    {!(__EXCLUDE_SALES__ && __EXCLUDE_RETURNS__) && (
      <>
        <Route
          path={customerContactCreatePath.pattern}
          element={<RequireAuth component={ContactFormView} onEnter={determineCustomerAccess} />}
        />
        <Route
          path={customerContactPath.pattern}
          element={<RequireAuth component={ContactFormView} onEnter={determineCustomerAccess} />}
        />
      </>
    )}
  </>
);
